/**
 * Cross-browser compatibility utilities
 */

// Check if browser supports specific features
export const browserFeatures = {
  // Check for IntersectionObserver support (for lazy loading)
  hasIntersectionObserver: () => typeof IntersectionObserver !== 'undefined',
  
  // Check for WebP support
  hasWebP: async (): Promise<boolean> => {
    if (!createImageBitmap) return false;
    
    const webpData = 'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=';
    const blob = await fetch(webpData).then(r => r.blob());
    
    return createImageBitmap(blob).then(
      () => true,
      () => false
    );
  },
  
  // Check for passive event listener support
  hasPassiveEvents: (): boolean => {
    let passiveSupported = false;
    
    try {
      const options = {
        get passive() {
          passiveSupported = true;
          return false;
        }
      };
      
      window.addEventListener('test', () => {}, options);
      window.removeEventListener('test', () => {}, options);
    } catch (err) {
      passiveSupported = false;
    }
    
    return passiveSupported;
  }
};

// Apply passive event listener based on browser support
export const addPassiveEventListener = (
  element: Element | Window | Document,
  eventName: string,
  handler: EventListenerOrEventListenerObject,
  capture = false
): void => {
  const passive = browserFeatures.hasPassiveEvents();
  element.addEventListener(
    eventName,
    handler,
    passive ? { passive: true, capture } : capture
  );
};

// Detect browser
export const getBrowser = (): string => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (userAgent.indexOf('edge') > -1) {
    return 'edge';
  } else if (userAgent.indexOf('edg') > -1) {
    return 'chromium-edge';
  } else if (userAgent.indexOf('opr') > -1 || userAgent.indexOf('opera') > -1) {
    return 'opera';
  } else if (userAgent.indexOf('chrome') > -1) {
    return 'chrome';
  } else if (userAgent.indexOf('safari') > -1) {
    return 'safari';
  } else if (userAgent.indexOf('firefox') > -1) {
    return 'firefox';
  } else if (userAgent.indexOf('msie') > -1 || userAgent.indexOf('trident') > -1) {
    return 'ie';
  } else {
    return 'unknown';
  }
};

// Apply specific browser fixes
export const applyBrowserFixes = (): void => {
  const browser = getBrowser();
  
  // Fix for Safari focus outline
  if (browser === 'safari') {
    const style = document.createElement('style');
    style.textContent = `
      a:focus, button:focus, input:focus, select:focus, textarea:focus {
        outline: none !important;
        box-shadow: 0 0 0 2px rgba(147, 51, 234, 0.5) !important;
      }
    `;
    document.head.appendChild(style);
  }
  
  // Fix for Firefox font rendering
  if (browser === 'firefox') {
    document.documentElement.style.fontSmooth = 'auto';
    document.documentElement.style.setProperty('-moz-osx-font-smoothing', 'auto');
  }
};